import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";
import NotFound from "pages/NotFound";
import UserAnalyticsHub from './pages/user-analytics-hub';
import RealTimeOperations from './pages/real-time-operations';
import ExecutiveOverview from './pages/executive-overview';
import FinancialPerformance from './pages/financial-performance';
import UserWalletDashboard from './pages/user-wallet-dashboard';
import SwapEngineInterface from './pages/swap-engine-interface';
import PaymentSpendingHub from './pages/payment-spending-hub';
import MerchantPaymentWidget from './pages/merchant-payment-widget';
import DigitalVisaCardManagement from './pages/digital-visa-card-management';
import ProPayoutManagement from './pages/pro-payout-management';
import TwoFactorAuthenticationSetup from './pages/two-factor-authentication-setup';
import TwoFactorAuthenticationManagement from './pages/two-factor-authentication-management';
import TwoFactorAuthenticationVerification from './pages/two-factor-authentication-verification';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import Profile from './pages/auth/Profile';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import PricingPlans from './pages/pricing-plans';
import SubscriptionManagement from './pages/subscription-management';
import BillingInvoices from './pages/billing-invoices';
import LegalDisclaimerTermsAgreement from './pages/legal-disclaimer-terms-agreement';
import MainAdminControlCenter from './pages/main-admin-control-center';
import AdminAuthenticationPortal from './pages/admin-authentication-portal';
import AdminSessionManagement from './pages/admin-session-management';
import EmailVerificationPortal from './pages/email-verification-portal';
import EmailNotificationPreferences from './pages/email-notification-preferences';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return children;
};

// Admin-only route protection
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin-authentication-portal" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/user-wallet-dashboard" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/email-verification-portal" element={<EmailVerificationPortal />} />
        <Route path="/legal-disclaimer-terms-agreement" element={<LegalDisclaimerTermsAgreement />} />
        <Route path="/two-factor-authentication-verification" element={<TwoFactorAuthenticationVerification />} />
        
        {/* Admin Authentication Portal - Public for admin login */}
        <Route path="/admin-authentication-portal" element={<AdminAuthenticationPortal />} />
        
        {/* Protected routes - Customer Interface */}
        <Route path="/" element={<ProtectedRoute><UserWalletDashboard /></ProtectedRoute>} />
        <Route path="/user-wallet-dashboard" element={<ProtectedRoute><UserWalletDashboard /></ProtectedRoute>} />
        <Route path="/swap-engine-interface" element={<ProtectedRoute><SwapEngineInterface /></ProtectedRoute>} />
        <Route path="/payment-spending-hub" element={<ProtectedRoute><PaymentSpendingHub /></ProtectedRoute>} />
        <Route path="/merchant-payment-widget" element={<ProtectedRoute><MerchantPaymentWidget /></ProtectedRoute>} />
        <Route path="/digital-visa-card-management" element={<ProtectedRoute><DigitalVisaCardManagement /></ProtectedRoute>} />
        <Route path="/pro-payout-management" element={<ProtectedRoute><ProPayoutManagement /></ProtectedRoute>} />
        <Route path="/two-factor-authentication-setup" element={<ProtectedRoute><TwoFactorAuthenticationSetup /></ProtectedRoute>} />
        <Route path="/two-factor-authentication-management" element={<ProtectedRoute><TwoFactorAuthenticationManagement /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/email-notification-preferences" element={<ProtectedRoute><EmailNotificationPreferences /></ProtectedRoute>} />
        <Route path="/pricing-plans" element={<PricingPlans />} />
        <Route path="/subscription-management" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
        <Route path="/billing-invoices" element={<ProtectedRoute><BillingInvoices /></ProtectedRoute>} />
        
        {/* Protected routes - Admin/Analytics - Require Admin Access */}
        <Route path="/main-admin-control-center" element={<AdminRoute><MainAdminControlCenter /></AdminRoute>} />
        <Route path="/admin-session-management" element={<AdminRoute><AdminSessionManagement /></AdminRoute>} />
        <Route path="/user-analytics-hub" element={<AdminRoute><UserAnalyticsHub /></AdminRoute>} />
        <Route path="/real-time-operations" element={<AdminRoute><RealTimeOperations /></AdminRoute>} />
        <Route path="/executive-overview" element={<AdminRoute><ExecutiveOverview /></AdminRoute>} />
        <Route path="/financial-performance" element={<AdminRoute><FinancialPerformance /></AdminRoute>} />
        
        {/* Catch all - 404 */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
